import { useEffect, useState } from 'react';

import { Checkbox, IconButton, Label, Select, TextField } from '@nike/eds';
import { useQuery } from '@tanstack/react-query';
import ProductWallResults from 'components/ProductWall/ProductWallResults';
import { SearchTermComboBox } from 'components/ProductWall/SearchTermComboBox';
import { QUERY_KEYS } from 'constants/index';
import { useAtom } from 'jotai';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { MultiValue } from 'react-select';
import * as R from 'ramda';
import {
  productWallErrorMessageAtom,
  productWallHasErrorAtom,
} from '../../atoms/productwall';
import searchOptionsVersionClient from '../../clientAPI/searchOptionsVersion';
import {
  SearchOptions,
  searchOptionsVerToSelectOpts,
  searchOptionsVerToVerMap,
  setSearchOptionsToURLSearchParams,
  unsetSearchOptionsToURLSearchParams,
} from '../../components/ProductWall/SearchOptions';
import parseArrayFromUrlString from '../../utils/parseArrayFromUrlString';
import ConceptSelect from 'components/ProductWall/ConceptSelect';

const channelOptions = [
  { value: '82a74ac1-c527-4470-b7b0-fb5f3ef3c2e2', label: 'Nike App' },
  { value: 'd9a5bc42-4b9c-4976-858a-f159cf99c647', label: 'Nike.COM' },
  { value: '15d92135-394e-487a-8afa-f6c8525c9ea9', label: 'WeChat' },
  { value: '008be467-6c78-4079-94f0-70e2d6cc4003', label: 'SNKRS' },
];

const languageOptions = [
  { value: 'zh-Hans', label: 'ZH-HANS' },
  { value: 'en', label: 'EN' },
];

const marketplaceOptions = [
  { value: 'CN', label: 'Greater China' },
  { value: 'US', label: 'United States' },
];

const suggestionFlowOptions = [
  { value: 'new', label: 'NEW' },
  { value: 'new_extend', label: 'NEW_EXTEND' },
  { value: 'old', label: 'OLD' },
];

const defaultSearchParams = {
  searchTerms: '',
  channel: channelOptions[0].value,
  language: languageOptions[0].value,
  marketplace: marketplaceOptions[0].value,
  callerId: 'search360UI',
  userAgent: 'search360UI',
  suggestion: suggestionFlowOptions[0].value,
  explain: 'false',
};

function useRouterAwareUseState(searchParamName: string, initState: any) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [val, setVal] = useState(initState);
  const updater = (newVal: any) => {
    setVal(newVal);
    searchParams.set(searchParamName, newVal);
    setSearchParams(searchParams);
  };
  return [val, updater];
}

export default function ProductWall() {
  const { status, data, error } = useQuery({
    queryKey: [QUERY_KEYS.SEARCH_OPTIONS_VERSION],
    queryFn: async () => searchOptionsVersionClient.getAll(),
    onError: (err: keyable) => {
      setErrorMsg(err.message || JSON.stringify(err));
      setHasError(true);
    },
    staleTime: Infinity,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  // debugger;
  const searchOptionsView = searchParams.get('searchOptionsView') === '1';

  const [showSearchOptions, setShowSearchOptions] = useState(searchOptionsView);
  const [searchVersion, setSearchVersion] = useState(
    searchParams.get('searchVer') || '',
  );

  const defaultSearchText =
    searchParams.get('searchTerms') || defaultSearchParams.searchTerms;

  const [searchTerms, setSearchTerms] = useState(
    searchParams.get('searchTerms') || defaultSearchParams.searchTerms,
  );
  const [consumerChannelId, setConsumerChannelId] = useRouterAwareUseState(
    'channel',
    searchParams.get('channel') || defaultSearchParams.channel,
  );
  const [language, setLanguage] = useRouterAwareUseState(
    'language',
    searchParams.get('language') || defaultSearchParams.language,
  );
  const [marketplace, setMarketplace] = useRouterAwareUseState(
    'marketplace',
    searchParams.get('marketplace') || defaultSearchParams.marketplace,
  );
  const [callerId, setCallerId] = useRouterAwareUseState(
    'callerId',
    searchParams.get('callerId') || defaultSearchParams.callerId,
  );
  const [userAgent, setUserAgent] = useRouterAwareUseState(
    'userAgent',
    searchParams.get('userAgent') || defaultSearchParams.userAgent,
  );
  const [suggestionFlow, setSuggestionFlow] = useRouterAwareUseState(
    'suggestion',
    searchParams.get('suggestion') || defaultSearchParams.suggestion,
  );

  const {
    status: searchSelectOptionsStatus,
    data: searchSelectOptionsData,
    error: searchSelectOptionsError,
  } = useQuery({
    queryKey: [QUERY_KEYS.SEARCH_SELECT_OPTIONS],
    queryFn: async () => searchOptionsVersionClient.getAllSearchOptions(),
    onError: (err: keyable) => {
      setErrorMsg(err.message || JSON.stringify(err));
      setHasError(true);
    },
    staleTime: Infinity,
  });

  const getDefaultSearchOptions = function (searchParams: URLSearchParams) {
    const defaultOptions = {} as { [key: string]: string };
    searchSelectOptionsData?.forEach((option) => {
      defaultOptions[option.key] =
        searchParams.get(option.key) ||
        option.options[option.defaultIndex].value;
    });
    return defaultOptions;
  };
  const [searchOptions, setSearchOptions] = useState(
    {} as { [key: string]: string },
  );

  const initTaxonomyIds = parseArrayFromUrlString(
    searchParams.get('taxonomyIds') || '',
  );
  const [taxonomyIds, setTaxonomyIds] =
    useState<Array<string>>(initTaxonomyIds);

  const [showExplainPanel, setShowExplainPanel] = useRouterAwareUseState(
    'explain',
    searchParams.get('explain') || defaultSearchParams.explain,
  );

  const [hasError, setHasError] = useAtom(productWallHasErrorAtom);
  const [errorMsg, setErrorMsg] = useAtom(productWallErrorMessageAtom);

  const handleSearchTermsValueChange = useDebouncedCallback((v: string) => {
    setSearchTerms(v);
    // reset taxonomyIds on searchTerms change
    setTaxonomyIds([]);
    searchParams.set('searchTerms', v);
    searchParams.delete('taxonomyIds');
    setSearchParams(searchParams);
  }, 1000);

  const handleAdvanceOptionsBtnClick = () => {
    if (showSearchOptions) {
      searchParams.delete('searchOptionsView');
      unsetSearchOptionsToURLSearchParams(searchOptions, searchParams);
    } else {
      searchParams.delete('searchVer');
      searchParams.set('searchOptionsView', '1');
      setSearchOptionsToURLSearchParams(searchOptions, searchParams);
    }
    setSearchParams(searchParams);

    setShowSearchOptions(!showSearchOptions);
  };
  const searchOptionsForQuery = showSearchOptions
    ? searchOptions
    : { version: searchVersion };
  const searchOptionsForExplainQuery =
    searchOptions.searchFlow === 'old' ? searchOptions : searchOptionsForQuery;

  useEffect(() => {
    if (searchSelectOptionsStatus == 'success') {
      setSearchOptions(getDefaultSearchOptions(searchParams));
    }

    //showSearchOptions &&
    if (!showSearchOptions && status === 'success') {
      const matchFromUrl = data.find(
        (v) => v.searchVersionNumber === searchVersion,
      );

      const defaultSearchVersions = data.filter((v) => v.default);

      const defaultSearchVersion =
        defaultSearchVersions.length > 0 ? defaultSearchVersions[0] : data[0];

      const opt = matchFromUrl ? matchFromUrl : defaultSearchVersion;

      if (opt) {
        setSearchVersion(opt.searchVersionNumber);
        setSearchOptions(opt.searchOptions);
      } else {
        setErrorMsg('No search options verion data available!');
        setHasError(true);
      }
    }
  }, [showSearchOptions, status, data]);

  const handleConceptSelectChange = (
    newSelectOpts: MultiValue<{ label: string; value: any }>,
  ) => {
    const selectedConceptIds = R.uniq(
      newSelectOpts
        .filter((opt) => opt.value && opt.value.id)
        .map((opt) => opt.value.id),
    );
    setTaxonomyIds(selectedConceptIds);
    if (selectedConceptIds.length === 0) {
      searchParams.set('taxonomyIds', '');
    } else {
      const urlVal = encodeURIComponent(JSON.stringify(selectedConceptIds));
      searchParams.set('taxonomyIds', urlVal);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className='tw-w-11/12 tw-mx-auto tw-py-10'>
      <div className='tw-mb-4'>
        <Label font='title-6' htmlFor='concept-select'>
          Concepts
        </Label>
        <ConceptSelect
          conceptIds={taxonomyIds}
          onChange={handleConceptSelectChange}
          selectConceptsRef={null}
          validationError={''}
          searchableOnly={false}
        />
      </div>
      <div
        className='tw-grid tw-items-end tw-gap-1'
        style={{
          gridTemplateColumns: 'repeat(auto-fit, 240px)',
          justifyItems: 'stretch',
        }}
      >
        <div style={{ gridArea: '1 / 1 / 2 / 3' }}>
          <SearchTermComboBox
            onValueChange={handleSearchTermsValueChange}
            language={language}
            marketplace={marketplace}
            suggestionFlow={suggestionFlow}
            defaultSearchText={defaultSearchText}
          />
        </div>
        <Select
          id='channel-select'
          className='tw-w-60 tw-rounded-3xl'
          label='Channel'
          options={channelOptions}
          value={channelOptions.find((v) => v.value === consumerChannelId)}
          onChange={(event) => {
            setConsumerChannelId(event?.value || '');
          }}
        />
        <Select
          id='suggestionFlow-select'
          className='tw-w-60 tw-rounded-3xl'
          label='Suggestion Flow'
          value={
            suggestionFlowOptions.filter((v) => v.value === suggestionFlow)[0]
          }
          options={suggestionFlowOptions}
          onChange={(event) => {
            setSuggestionFlow(event?.value || '');
          }}
        />
        {searchSelectOptionsStatus === 'success' &&
          status === 'success' &&
          data && (
            <Select
              isDisabled={showSearchOptions}
              id='search-ver-select'
              className='tw-w-60 tw-rounded-3xl'
              label='SearchOptions By Version'
              options={searchOptionsVerToSelectOpts(data)}
              value={searchOptionsVerToSelectOpts(data).find(
                (v) => v.value === searchVersion,
              )}
              onChange={(event) => {
                const verKey = event?.value || '';
                setSearchVersion(verKey);
                searchParams.set('searchVer', verKey);
                setSearchParams(searchParams);
                const valMap = searchOptionsVerToVerMap(data);
                verKey &&
                  valMap[verKey] &&
                  setSearchOptions(
                    valMap[verKey]?.searchOptions as { [key: string]: string },
                  );
              }}
            />
          )}
        <div className='tw-mx-3'>
          <Checkbox
            className='tw-mr-3'
            id='toggle-explain'
            name='explain'
            label='Explain'
            checked={showExplainPanel === 'true'}
            onChange={(v) => setShowExplainPanel(v.target.checked + '')}
          />
          <IconButton
            label=''
            variant='ghost'
            icon={showSearchOptions ? 'Collapse' : 'Expand'}
            onClick={handleAdvanceOptionsBtnClick}
          />
        </div>
      </div>
      {showSearchOptions && (
        <div
          className='tw-grid tw-items-center tw-gap-1 tw-mt-3'
          style={{
            gridTemplateColumns: 'repeat(auto-fit, 240px)',
            justifyItems: 'stretch',
          }}
        >
          <SearchOptions
            searchOptions={searchOptions}
            setSearchOptions={setSearchOptions}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            searchSelectOptionsData={searchSelectOptionsData}
          />
          <Select
            id='language-select'
            className='tw-w-60 tw-rounded-3xl'
            label='Language'
            options={languageOptions}
            value={languageOptions.find((v) => v.value === language)}
            onChange={(event) => {
              setLanguage(event?.value || '');
            }}
          />
          <Select
            id='marketplace-select'
            className='tw-w-60 tw-rounded-3xl'
            label='Marketplace'
            options={marketplaceOptions}
            value={marketplaceOptions.find((v) => v.value === marketplace)}
            onChange={(event) => {
              setMarketplace(event?.value || '');
            }}
          />
          <TextField
            id='callerId'
            type='text'
            label='Caller ID'
            placeholder='Please input Caller ID'
            value={callerId}
            onChange={(event) => {
              setCallerId(event.target.value);
            }}
          />
          <TextField
            id='userAgent'
            type='text'
            label='User Agent'
            placeholder='Please input User Agent'
            value={userAgent}
            onChange={(event) => {
              setUserAgent(event.target.value);
            }}
          />
        </div>
      )}
      <br />
      {status === 'success' && (
        <ProductWallResults
          searchTerms={searchTerms}
          marketplace={marketplace}
          searchOptions={searchOptionsForQuery}
          showExplainPanel={showExplainPanel}
          searchOptionsForExplainQuery={searchOptionsForExplainQuery}
          language={language}
          callerId={callerId}
          userAgent={userAgent}
          consumerChannelId={consumerChannelId}
          taxonomyIds={taxonomyIds}
          setTaxonomyIds={setTaxonomyIds}
        />
      )}
    </div>
  );
}
